<script setup>
import { ref, watch } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import WhatsappCampaignsAPI from 'dashboard/api/whatsappCampaigns';

const props = defineProps({
  campaign: { type: Object, required: true },
  isOpen: { type: Boolean, default: false },
});

const emit = defineEmits(['close']);

const replies = ref([]);
const isLoading = ref(false);
const currentPage = ref(1);
const totalPages = ref(0);
const totalCount = ref(0);

const fetchReplies = async (page = 1) => {
  isLoading.value = true;
  try {
    const { data } = await WhatsappCampaignsAPI.getReplies(props.campaign.id, {
      page,
      per_page: 25,
    });
    replies.value = data.replies;
    currentPage.value = data.meta.current_page;
    totalPages.value = data.meta.total_pages;
    totalCount.value = data.meta.total_count;
  } catch {
    replies.value = [];
  } finally {
    isLoading.value = false;
  }
};

watch(
  () => props.isOpen,
  open => {
    if (open) fetchReplies(1);
  },
  { immediate: true }
);

const formatDate = date => {
  if (!date) return '-';
  return new Date(date).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const openConversation = reply => {
  const accountId = props.campaign.account_id;
  const inboxId = props.campaign.inbox_id || props.campaign.inbox?.id;
  const url = `/app/accounts/${accountId}/conversations/${reply.display_id}`;
  window.open(url, '_blank');
};
</script>

<template>
  <teleport to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 flex items-center justify-center bg-n-alpha-5"
      @click.self="emit('close')"
    >
      <div
        class="bg-n-solid-1 rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col"
      >
        <!-- Header -->
        <div
          class="flex items-center justify-between px-6 py-4 border-b border-n-weak"
        >
          <div>
            <h2 class="text-lg font-semibold text-n-slate-12">
              Respostas da Campanha
            </h2>
            <p class="text-sm text-n-slate-10 mt-0.5">
              {{ campaign.title }} — {{ totalCount }} respostas
            </p>
          </div>
          <Button
            icon="i-lucide-x"
            size="sm"
            variant="ghost"
            color="slate"
            @click="emit('close')"
          />
        </div>

        <!-- Content -->
        <div class="flex-1 overflow-auto">
          <div
            v-if="isLoading"
            class="flex items-center justify-center py-10"
          >
            <Spinner />
          </div>

          <div
            v-else-if="replies.length === 0"
            class="flex flex-col items-center justify-center py-10 text-n-slate-10"
          >
            <span class="i-lucide-message-circle-off text-3xl mb-2" />
            <p>Nenhuma resposta recebida ainda</p>
          </div>

          <div v-else class="divide-y divide-n-weak">
            <div
              v-for="reply in replies"
              :key="reply.conversation_id"
              class="px-6 py-3 hover:bg-n-alpha-1 cursor-pointer flex items-center gap-4"
              @click="openConversation(reply)"
            >
              <div
                class="w-10 h-10 rounded-full bg-n-alpha-3 flex items-center justify-center flex-shrink-0"
              >
                <span class="text-sm font-semibold text-n-slate-11">
                  {{ (reply.contact_name || '?')[0].toUpperCase() }}
                </span>
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2">
                  <span class="font-medium text-sm text-n-slate-12 truncate">
                    {{ reply.contact_name || 'Sem nome' }}
                  </span>
                  <span class="text-xs text-n-slate-9">
                    {{ reply.formatted_phone }}
                  </span>
                </div>
                <p class="text-xs text-n-slate-10 truncate mt-0.5">
                  {{ reply.last_reply || '-' }}
                </p>
              </div>
              <div class="text-xs text-n-slate-9 whitespace-nowrap">
                {{ formatDate(reply.last_reply_at) }}
              </div>
              <span
                class="i-lucide-external-link text-n-slate-9 flex-shrink-0"
              />
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div
          v-if="totalPages > 1"
          class="flex items-center justify-between px-6 py-3 border-t border-n-weak"
        >
          <Button
            :disabled="currentPage <= 1"
            size="sm"
            variant="ghost"
            icon="i-lucide-chevron-left"
            @click="fetchReplies(currentPage - 1)"
          >
            Anterior
          </Button>
          <span class="text-xs text-n-slate-10">
            Página {{ currentPage }} de {{ totalPages }}
          </span>
          <Button
            :disabled="currentPage >= totalPages"
            size="sm"
            variant="ghost"
            icon="i-lucide-chevron-right"
            @click="fetchReplies(currentPage + 1)"
          >
            Próxima
          </Button>
        </div>
      </div>
    </div>
  </teleport>
</template>
